import { Building2, ChevronDown, History, Loader2, Pencil, RotateCw, ShieldAlert, Trash2, UserPlus, Users, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  adminClient,
  type StudioTeamAccessResponse,
  type StudioTeamChange,
  type StudioTeamInvitation,
  type StudioTeamInvitationsPage,
  type StudioTeamInviteResult,
  type StudioTeamOrg,
  type StudioTeamPeoplePage,
  type StudioTeamPerson,
  type StudioTeamRoleKey,
} from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { SearchInput } from '@/components/table/SearchInput';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Sheet } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

import { RoleSections } from './RoleSections';
import {
  type AccessFilter,
  type CannotManage,
  PEOPLE_PAGE_SIZES,
  PERMISSION_FILTER_PREFIX,
  PLATFORM_ADMIN_ROLE_KEY,
  type RoleCatalog,
  SEARCH_MIN_CHARS,
  USER_CHANGES_PATH,
  bulkGrantDescription,
  cannotManageRoles,
  changeLine,
  changesToConfirm,
  editLocks,
  invitationMeta,
  inviteFormProblem,
  inviteResultText,
  orgOptionLabel,
  parseEmailList,
  permissionLabel,
  permissionOrder,
  personLabel,
  pickOrg,
  planRoleChanges,
  rangeLabel,
  readRememberedOrg,
  rememberOrg,
  roleCatalog,
  roleFilterLabel,
  roleLabel,
  roleSections,
  roleSummary,
  searchQuery,
  withoutRedundant,
  userDetailPath,
} from './usersModel';

function Avatar({ person }: { person: StudioTeamPerson }) {
  if (person.picture) {
    return <img src={person.picture} alt="" className="h-8 w-8 shrink-0 rounded-full bg-muted" />;
  }
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
      {personLabel(person).charAt(0).toUpperCase()}
    </div>
  );
}

/**
 * A person's roles at a glance: their bundles (and Platform Admin) as the main
 * badges, any single permissions a bundle does not cover as plain ones, and a
 * flag when some are redundant. The tooltip lists the effective permissions.
 */
function RoleSummaryBadges({ person, catalog }: { person: StudioTeamPerson; catalog: RoleCatalog }) {
  const { main, extra, more, redundant, effective } = roleSummary(person, catalog);
  if (main.length + extra.length === 0) return <span className="text-xs text-muted-foreground">No Studio roles</span>;
  return (
    <span className="flex flex-wrap items-center gap-1" title={`Effective permissions: ${effective.join(', ') || 'none'}`}>
      {main.map((label) => (
        <span key={label} className="rounded-full border border-brand/40 bg-brand-muted px-2 py-0.5 text-xs font-medium text-brand">
          {label}
        </span>
      ))}
      {extra.map((label) => (
        <span key={label} className="rounded-full border border-border px-2 py-0.5 text-xs font-medium text-foreground">
          {label}
        </span>
      ))}
      {more > 0 ? <span className="rounded-full border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground">+{more}</span> : null}
      {redundant > 0 ? <StatusPill tone="warning">{redundant} redundant</StatusPill> : null}
    </span>
  );
}

type FilterSection = { key: string; label: string; entries: Array<{ key: string; label: string }> };

/** The role filter's sections: roles held (bundles and Platform Admin), single permissions held, and effective permissions. */
export function roleFilterSections(catalog: RoleCatalog): FilterSection[] {
  const { bundles, platformAdmin, advanced } = roleSections(catalog);
  const permissions = permissionOrder(catalog);
  return [
    { key: 'roles', label: 'Role', entries: [...bundles, ...(platformAdmin ? [platformAdmin] : [])].map((role) => ({ key: role.key, label: role.label })) },
    { key: 'singles', label: 'Individual permission held', entries: advanced.map((role) => ({ key: role.key, label: role.label })) },
    {
      key: 'effective',
      label: 'Effective permission (any role)',
      entries: permissions.map((permission) => ({ key: `${PERMISSION_FILTER_PREFIX}${permission}`, label: permissionLabel(catalog, permission) })),
    },
  ].filter((section) => section.entries.length > 0);
}

/** "Has role: …": show only people holding any of the picked roles. */
export function RoleFilter({
  catalog,
  selected,
  onChange,
}: {
  catalog: RoleCatalog;
  selected: StudioTeamRoleKey[];
  onChange: (keys: StudioTeamRoleKey[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = selected.length === 0 ? 'Any' : selected.length === 1 ? roleFilterLabel(catalog, selected[0]) : `${selected.length} picked`;
  return (
    <div ref={rootRef} className="relative">
      <Button type="button" variant="outline" size="sm" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        Has role: {label}
        <ChevronDown className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
      </Button>
      {open ? (
        <div role="group" aria-label="Has role" className="absolute right-0 z-20 mt-1 max-h-96 w-72 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border border-border bg-card p-2 shadow-lg">
          {roleFilterSections(catalog).map((section) => (
            <div key={section.key} className="py-1">
              <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{section.label}</p>
              {section.entries.map((entry) => (
                <label key={entry.key} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50">
                  <input
                    type="checkbox"
                    checked={selected.includes(entry.key)}
                    onChange={(event) => onChange(event.target.checked ? [...selected, entry.key] : selected.filter((key) => key !== entry.key))}
                    className="h-4 w-4 cursor-pointer accent-primary"
                  />
                  {entry.label}
                </label>
              ))}
            </div>
          ))}
          {selected.length > 0 ? (
            <button type="button" onClick={() => onChange([])} className="mt-1 w-full cursor-pointer rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted/50">
              Clear
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CannotManagePanel({ problem }: { problem: CannotManage }) {
  return (
    <section role="alert" className="mb-6 rounded-xl border border-warning/30 bg-warning-bg p-5 text-sm">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning-foreground" aria-hidden="true" />
        <div className="min-w-0 space-y-2">
          <h2 className="font-semibold text-foreground">Studio can&apos;t manage roles yet</h2>
          <p className="text-muted-foreground">
            {problem.notConfigured
              ? 'WorkOS is not configured on the Studio API.'
              : 'WorkOS refused the request: check the Studio API’s WorkOS API key.'}{' '}
            Until it is fixed, roles are still assigned by hand in the WorkOS dashboard.
          </p>
          <p className="text-xs text-muted-foreground">Then reload this page. No restart is needed.</p>
        </div>
      </div>
    </section>
  );
}

function RoleTags({ roles, catalog }: { roles: StudioTeamRoleKey[]; catalog: RoleCatalog }) {
  if (roles.length === 0) return <span className="text-xs text-muted-foreground">No Studio roles</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((key) => (
        <span key={key} title={catalog.byKey.get(key)?.description} className="rounded-full border border-brand/40 bg-brand-muted px-2 py-0.5 text-xs font-medium text-brand">
          {roleLabel(catalog, key)}
        </span>
      ))}
    </span>
  );
}

export function invitationKey(invitation: Pick<StudioTeamInvitation, 'orgId' | 'email'>): string {
  return `invite\n${invitation.orgId}\n${invitation.email}`;
}

function PendingInvitations({
  page,
  catalog,
  busyKey,
  onPageChange,
  onResend,
  onRevoke,
}: {
  page: StudioTeamInvitationsPage;
  catalog: RoleCatalog;
  busyKey?: string;
  onPageChange: (page: number) => void;
  onResend: (invitation: StudioTeamInvitation) => void;
  onRevoke: (invitation: StudioTeamInvitation) => void;
}) {
  return (
    <section aria-label="Pending invitations" className="mb-6 overflow-hidden rounded-xl border border-border bg-card">
      <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-foreground">
        Pending invitations <span className="font-normal tabular-nums text-muted-foreground">{page.total}</span>
      </h2>
      <ul className="divide-y divide-border">
        {page.invitations.map((invitation) => {
          const busy = busyKey === invitationKey(invitation);
          return (
            <li key={invitationKey(invitation)} className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:gap-4">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                  <span className="truncate">{invitation.email}</span>
                  {invitation.expired ? <StatusPill tone="warning">Expired</StatusPill> : null}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{invitationMeta(invitation)}</p>
              </div>
              <RoleTags roles={invitation.roles} catalog={catalog} />
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" disabled={Boolean(busyKey)} onClick={() => onResend(invitation)}>
                  {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <RotateCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />}
                  Resend
                </Button>
                <Button type="button" variant="ghost" size="sm" disabled={Boolean(busyKey)} onClick={() => onRevoke(invitation)}>
                  <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  Revoke
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      {page.total > page.pageSize ? (
        <TablePager
          total={page.total}
          limit={page.pageSize}
          offset={(page.page - 1) * page.pageSize}
          onOffsetChange={(offset) => onPageChange(Math.floor(offset / page.pageSize) + 1)}
          label={rangeLabel(page.page, page.pageSize, page.total, 'invitations')}
        />
      ) : null}
    </section>
  );
}

export type InviteFormProps = {
  orgs: StudioTeamOrg[];
  catalog: RoleCatalog;
  text: string;
  onTextChange: (text: string) => void;
  orgId?: string;
  onOrgChange: (orgId: string) => void;
  roles: StudioTeamRoleKey[];
  onToggleRole: (key: StudioTeamRoleKey) => void;
  /** Drop single permissions a picked bundle already grants. */
  onCleanUpRoles?: () => void;
  showProblem: boolean;
  results?: StudioTeamInviteResult[];
  busyEmail?: string;
  onAddDirectly: (email: string) => void;
  onInviteAnyway: (email: string) => void;
};

/** The invite dialog's body, without the dialog, so it can render from props alone. */
export function InviteForm({
  orgs,
  catalog,
  text,
  onTextChange,
  orgId,
  onOrgChange,
  roles,
  onToggleRole,
  onCleanUpRoles = () => undefined,
  showProblem,
  results,
  busyEmail,
  onAddDirectly,
  onInviteAnyway,
}: InviteFormProps) {
  const orgName = orgs.find((org) => org.id === orgId)?.name ?? 'the organization';
  if (results) {
    return (
      <ul className="space-y-3 text-sm" aria-label="Invitation results">
        {results.map((result) => (
          <li key={result.email} className="space-y-1.5">
            <p className="font-medium text-foreground">{result.email}</p>
            <p className={cn('text-muted-foreground', result.status === 'failed' && 'text-destructive-foreground')}>{inviteResultText(result, orgName, catalog)}</p>
            {result.status === 'exists' ? (
              <div className="flex flex-wrap gap-2">
                {result.canAddDirectly ? (
                  <Button type="button" size="sm" disabled={Boolean(busyEmail)} onClick={() => onAddDirectly(result.email)}>
                    {busyEmail === result.email ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
                    Add to {orgName}
                  </Button>
                ) : null}
                <Button type="button" variant="outline" size="sm" disabled={Boolean(busyEmail)} onClick={() => onInviteAnyway(result.email)}>
                  Send invitation
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    );
  }
  const problem = showProblem ? inviteFormProblem(text, orgId) : undefined;
  const count = parseEmailList(text).emails.length;
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="invite-emails">Email addresses</Label>
        <Textarea
          id="invite-emails"
          value={text}
          onChange={(event) => onTextChange(event.target.value)}
          placeholder="name@rapidsos.com, another@rapidsos.com"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          aria-invalid={Boolean(problem)}
          aria-describedby="invite-emails-hint"
          className="text-base sm:text-sm"
        />
        <p id="invite-emails-hint" className={cn('text-xs text-muted-foreground', problem && 'text-destructive-foreground')}>
          {problem ?? (count > 1 ? `${count} people. Separate addresses with commas or new lines.` : 'Separate addresses with commas or new lines.')}
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="invite-org">Organization</Label>
        {orgs.length === 1 ? (
          <p id="invite-org" className="text-sm text-foreground">{orgs[0].name}</p>
        ) : (
          <select
            id="invite-org"
            value={orgId ?? ''}
            onChange={(event) => onOrgChange(event.target.value)}
            className="flex h-10 w-full cursor-pointer rounded-md border border-input bg-background px-3 text-base text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-sm"
          >
            <option value="" disabled>
              Pick a RapidSOS organization
            </option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="space-y-1.5">
        <p className="text-sm font-medium leading-none">Studio roles</p>
        <p className="text-xs text-muted-foreground">Given once they join and sign in. Internal Access is the usual start.</p>
        <RoleSections
          catalog={catalog}
          selected={new Set(roles)}
          onToggle={(key) => onToggleRole(key)}
          onCleanUp={onCleanUpRoles}
          locks={Object.fromEntries(catalog.roles.filter((role) => !role.available).map((role) => [role.key, `WorkOS has no "${role.name}" role yet.`]))}
        />
      </div>
    </div>
  );
}

/** Pick the internal organization the list shows. One organization is shown by name. */
export function OrgSwitcher({ orgs, orgId, onChange }: { orgs: StudioTeamOrg[]; orgId?: string; onChange: (orgId: string) => void }) {
  if (orgs.length === 0) return null;
  if (orgs.length === 1) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Building2 className="h-4 w-4" aria-hidden="true" />
        {orgOptionLabel(orgs[0])}
      </p>
    );
  }
  return (
    <label className="flex w-full items-center gap-2 text-sm text-muted-foreground sm:w-auto">
      <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="shrink-0">Organization</span>
      <select
        aria-label="Organization"
        value={orgId ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className="flex h-10 min-w-0 flex-1 cursor-pointer rounded-md border border-input bg-background px-3 text-base text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-72 sm:flex-none sm:text-sm"
      >
        {orgs.map((org) => (
          <option key={org.id} value={org.id}>
            {orgOptionLabel(org)}
          </option>
        ))}
      </select>
    </label>
  );
}

/** "N people outside RapidSOS hold Studio roles": kept out of the list, reviewed on their own. */
function OutsidersBanner({ count, onReview }: { count: number; onReview?: () => void }) {
  return (
    <div role="status" className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-warning/30 bg-warning-bg px-4 py-3 text-sm">
      <ShieldAlert className="h-4 w-4 shrink-0 text-warning-foreground" aria-hidden="true" />
      <span className="min-w-0 flex-1 text-foreground">
        {count === 1 ? '1 person outside RapidSOS holds Studio roles.' : `${count} people outside RapidSOS hold Studio roles.`}{' '}
        <span className="text-muted-foreground">They can only have roles removed.</span>
      </span>
      {onReview ? (
        <Button type="button" variant="outline" size="sm" onClick={onReview}>
          Review
        </Button>
      ) : null}
    </div>
  );
}

/** The latest few changes, one line each, and the way to the full log. */
function RecentChanges({ changes, catalog }: { changes: StudioTeamChange[]; catalog: RoleCatalog }) {
  return (
    <AdminSection
      className="mt-6"
      title="Recent changes"
      description="Roles given, removed or promised with an invitation from this page."
      bodyClassName="p-0"
      actions={
        <Button asChild variant="outline" size="sm">
          <Link to={USER_CHANGES_PATH}>
            <History className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            View all
          </Link>
        </Button>
      }
    >
      {changes.length === 0 ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">No changes yet.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {changes.map((change, index) => (
            <li key={change.id ?? `${change.at}-${index}`} className="flex min-w-0 items-baseline gap-3 px-5 py-2">
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatDateTime(change.at)}</span>
              <span className="min-w-0 truncate text-foreground" title={changeLine(change, catalog)}>
                {changeLine(change, catalog)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </AdminSection>
  );
}

export type UsersViewProps = {
  data?: StudioTeamAccessResponse;
  loading: boolean;
  loadError?: string;
  cannotManage?: CannotManage;
  orgId?: string;
  onOrgChange?: (orgId: string) => void;
  people?: StudioTeamPeoplePage;
  peopleLoading?: boolean;
  peopleError?: string;
  filter: AccessFilter;
  onFilterChange: (filter: AccessFilter) => void;
  query: string;
  onQueryChange: (query: string) => void;
  search: string;
  roleFilter?: StudioTeamRoleKey[];
  onRoleFilterChange?: (keys: StudioTeamRoleKey[]) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  onPageChange?: (page: number) => void;
  invitations?: StudioTeamInvitationsPage;
  onInvitationsPageChange?: (page: number) => void;
  busyKey?: string;
  onEditRoles: (person: StudioTeamPerson) => void;
  onRetry: () => void;
  onInvite?: () => void;
  /** "Give Internal Access to everyone" in the organization shown: asks first, with the count. */
  onGrantDefaultToEveryone?: () => void;
  onResend?: (invitation: StudioTeamInvitation) => void;
  onRevoke?: (invitation: StudioTeamInvitation) => void;
  /** Open the people outside RapidSOS who hold a role, to remove it. */
  onReviewOutsiders?: () => void;
};

/** The page without its data fetching, so it can render from props alone. */
export function UsersView({
  data,
  loading,
  loadError,
  cannotManage,
  orgId,
  onOrgChange = () => undefined,
  people,
  peopleLoading = false,
  peopleError,
  filter,
  onFilterChange,
  query,
  onQueryChange,
  search,
  roleFilter = [],
  onRoleFilterChange = () => undefined,
  pageSize = PEOPLE_PAGE_SIZES[0],
  onPageSizeChange = () => undefined,
  onPageChange = () => undefined,
  invitations,
  onInvitationsPageChange = () => undefined,
  busyKey,
  onEditRoles,
  onRetry,
  onInvite,
  onGrantDefaultToEveryone,
  onResend = () => undefined,
  onRevoke = () => undefined,
  onReviewOutsiders,
}: UsersViewProps) {
  const catalog = useMemo(() => roleCatalog(data), [data]);
  const orgs = data?.orgs ?? [];
  const orgName = orgs.find((org) => org.id === orgId)?.name ?? 'this organization';
  const defaultRole = data?.defaultRoleKey ? catalog.byKey.get(data.defaultRoleKey) : undefined;
  const missingDefault = people?.missingDefault ?? 0;
  const canInvite = Boolean(onInvite && !cannotManage && orgs.length > 0);
  const rows = people?.people ?? [];

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Users"
        description="People in RapidSOS's organizations and their Studio roles."
        actions={
          onInvite || onGrantDefaultToEveryone ? (
            <div className="flex flex-wrap justify-end gap-2">
              {onGrantDefaultToEveryone && defaultRole ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onGrantDefaultToEveryone}
                  disabled={Boolean(cannotManage) || !defaultRole.available || !people || missingDefault === 0}
                  title={people && missingDefault === 0 ? `Everyone in ${orgName} has ${defaultRole.label}.` : `Everyone in ${orgName} who does not have it yet.`}
                  className="shrink-0"
                >
                  <Users className="mr-2 h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Give {defaultRole.label} to everyone</span>
                  <span className="sm:hidden">{defaultRole.label} for all</span>
                </Button>
              ) : null}
              {onInvite ? (
                <Button type="button" onClick={onInvite} disabled={!canInvite} className="shrink-0">
                  <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Invite
                </Button>
              ) : null}
            </div>
          ) : null
        }
      />

      {cannotManage ? <CannotManagePanel problem={cannotManage} /> : null}
      {!cannotManage && loadError && !data ? (
        <div role="alert" className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-destructive-border bg-destructive-bg px-4 py-3 text-sm text-destructive-foreground">
          <span className="min-w-0 flex-1">{loadError}</span>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </div>
      ) : null}

      {data ? (
        <div className="mb-4">
          <OrgSwitcher orgs={orgs} orgId={orgId} onChange={onOrgChange} />
        </div>
      ) : null}

      {data?.outsiderCount ? <OutsidersBanner count={data.outsiderCount} onReview={onReviewOutsiders} /> : null}

      {invitations && invitations.total > 0 ? (
        <PendingInvitations
          page={invitations}
          catalog={catalog}
          busyKey={busyKey}
          onPageChange={onInvitationsPageChange}
          onResend={onResend}
          onRevoke={onRevoke}
        />
      ) : null}

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <SearchInput
            value={query}
            onChange={onQueryChange}
            placeholder={`Search name or email (${SEARCH_MIN_CHARS}+ letters)`}
            label="Search people"
          />
          <SegmentedFilter<AccessFilter>
            label="Who to show"
            value={filter}
            onChange={onFilterChange}
            options={[
              { id: 'access', label: 'Has a Studio role', count: people?.counts.access },
              { id: 'everyone', label: 'Everyone', count: people?.counts.everyone },
            ]}
          />
          {data ? <RoleFilter catalog={catalog} selected={roleFilter} onChange={onRoleFilterChange} /> : null}
          <label className="flex items-center gap-2 text-xs text-muted-foreground sm:ml-auto">
            Per page
            <select
              aria-label="People per page"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              className="h-9 cursor-pointer rounded-md border border-input bg-background px-2 text-base text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-sm"
            >
              {PEOPLE_PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>
        {(loading && !data) || (peopleLoading && !people) ? (
          <p className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading people
          </p>
        ) : peopleError && !people ? (
          <div role="alert" className="flex flex-wrap items-center gap-3 px-4 py-6 text-sm text-destructive-foreground">
            <span className="min-w-0 flex-1">{peopleError}</span>
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : !data ? null : data.internalOrgCount === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">No RapidSOS organizations are configured (STUDIO_INTERNAL_ORG_IDS).</p>
        ) : !people ? null : rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {search.length >= SEARCH_MIN_CHARS || roleFilter.length > 0
              ? 'Nobody matches.'
              : filter === 'access'
                ? `Nobody in ${orgName} holds a Studio role yet.`
                : 'No people found.'}
          </p>
        ) : (
          <ul className={cn('divide-y divide-border', peopleLoading && 'opacity-60')} aria-busy={peopleLoading}>
            {rows.map((person) => (
              <li key={person.userId} className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Avatar person={person} />
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-medium text-foreground">
                      <Link to={userDetailPath(person.userId) ?? '/users'} className="cursor-pointer truncate underline-offset-2 hover:underline" aria-label={`Open ${personLabel(person)}’s activity`}>
                        {personLabel(person)}
                      </Link>
                      {person.isYou ? <StatusPill tone="neutral">You</StatusPill> : null}
                    </p>
                    {person.name && person.email ? <p className="truncate text-xs text-muted-foreground">{person.email}</p> : null}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 md:justify-end">
                  <RoleSummaryBadges person={person} catalog={catalog} />
                  <Button type="button" variant="outline" size="sm" onClick={() => onEditRoles(person)} aria-label={`Edit roles for ${personLabel(person)}`}>
                    <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                    Edit roles
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {people && people.total > 0 ? (
          <TablePager
            total={people.total}
            limit={people.pageSize}
            offset={(people.page - 1) * people.pageSize}
            onOffsetChange={(offset) => onPageChange(Math.floor(offset / people.pageSize) + 1)}
            label={rangeLabel(people.page, people.pageSize, people.total)}
            disabled={peopleLoading}
          />
        ) : null}
      </div>

      {data ? <RecentChanges changes={data.recent} catalog={catalog} /> : null}
    </div>
  );
}

export type OutsidersSheetProps = {
  open: boolean;
  people?: StudioTeamPerson[];
  loading: boolean;
  error?: string;
  catalog: RoleCatalog;
  /** The person whose roles are about to be removed, once Remove roles is pressed. */
  confirming?: StudioTeamPerson;
  removing: boolean;
  onAskRemove: (person: StudioTeamPerson) => void;
  onCancelRemove: () => void;
  onRemove: (person: StudioTeamPerson) => void;
  onClose: () => void;
};

/** The roles this person holds that can be taken away from here. */
export function removableRoles(person: StudioTeamPerson): StudioTeamRoleKey[] {
  return person.roles.filter((role) => role.removable).map((role) => role.key);
}

/** The outsiders panel's body, without the panel, so it can render from props alone. */
export function OutsidersList({ people, loading, error, catalog, confirming, onAskRemove }: Omit<OutsidersSheetProps, 'open' | 'onClose' | 'removing' | 'onCancelRemove' | 'onRemove'>) {
  if (confirming) {
    return (
      <div className="space-y-3 text-sm" role="alert">
        <p className="font-medium text-foreground">Remove these roles from {personLabel(confirming)}?</p>
        <RoleTags roles={removableRoles(confirming)} catalog={catalog} />
        <p className="text-xs text-muted-foreground">It applies within about a minute. They cannot be given a role again from here.</p>
      </div>
    );
  }
  if (loading && !people) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading
      </p>
    );
  }
  if (error && !people) return <p role="alert" className="text-sm text-destructive-foreground">{error}</p>;
  if (!people || people.length === 0) return <p className="text-sm text-muted-foreground">Nobody outside RapidSOS holds a Studio role.</p>;
  return (
    <ul className="divide-y divide-border" aria-label="People outside RapidSOS">
      {people.map((person) => {
        const removable = removableRoles(person);
        return (
          <li key={person.userId} className="space-y-2 py-3">
            <div className="flex items-center gap-3">
              <Avatar person={person} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{personLabel(person)}</p>
                {person.name && person.email ? <p className="truncate text-xs text-muted-foreground">{person.email}</p> : null}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onAskRemove(person)}
                disabled={removable.length === 0}
                title={removable.length === 0 ? 'The last Platform Admin, or your own Platform Admin, cannot be removed.' : undefined}
                aria-label={`Remove roles from ${personLabel(person)}`}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                Remove roles
              </Button>
            </div>
            <RoleTags roles={person.roles.map((role) => role.key)} catalog={catalog} />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * People in no RapidSOS organization who hold a Studio role. Nothing can be
 * given to them here: the only action is removing their roles.
 */
export function OutsidersSheet({ open, removing, onCancelRemove, onRemove, onClose, ...body }: OutsidersSheetProps) {
  const confirmingRoles = body.confirming ? removableRoles(body.confirming) : [];
  const confirming = body.confirming;
  return (
    <Sheet
      open={open}
      onClose={removing ? () => undefined : onClose}
      title="People outside RapidSOS"
      description="They hold Studio roles but are in none of RapidSOS's organizations. Roles can only be removed here."
      footer={
        confirming ? (
          <>
            <Button type="button" variant="outline" onClick={onCancelRemove} disabled={removing}>
              Back
            </Button>
            <Button type="button" variant="destructive" onClick={() => onRemove(confirming)} disabled={removing || confirmingRoles.length === 0}>
              {removing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Remove {confirmingRoles.length === 1 ? 'role' : `${confirmingRoles.length} roles`}
            </Button>
          </>
        ) : undefined
      }
    >
      <OutsidersList {...body} />
    </Sheet>
  );
}

export type EditRolesProps = {
  person?: StudioTeamPerson;
  catalog: RoleCatalog;
  saving: boolean;
  onClose: () => void;
  onSave: (person: StudioTeamPerson, change: { add: StudioTeamRoleKey[]; remove: StudioTeamRoleKey[] }) => void;
};

/**
 * Edit roles: the RapidSOS bundles first, Platform Admin on its own, the single
 * permissions under Advanced, what they add up to, and one Save for all the
 * changes. Removals, and giving a role that asks (Platform Admin, Support,
 * Admin Access), are confirmed inside the panel before anything changes.
 */
export function EditRolesSheet({ person, catalog, saving, onClose, onSave }: EditRolesProps) {
  const [selected, setSelected] = useState<Set<StudioTeamRoleKey>>(new Set());
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setSelected(new Set(person?.roles.map((role) => role.key) ?? []));
    setConfirming(false);
  }, [person]);

  const holdsSingles = Boolean(person?.roles.some((role) => catalog.byKey.get(role.key)?.kind !== 'bundle' && role.key !== PLATFORM_ADMIN_ROLE_KEY));

  const plan = person ? planRoleChanges(person, selected) : { add: [], remove: [] };
  const toConfirm = changesToConfirm(plan, catalog);
  const changed = plan.add.length + plan.remove.length > 0;
  const save = () => {
    if (!person || !changed) return;
    if (toConfirm.length > 0 && !confirming) {
      setConfirming(true);
      return;
    }
    onSave(person, plan);
  };

  return (
    <Sheet
      open={Boolean(person)}
      onClose={saving ? () => undefined : onClose}
      title={person ? `Roles for ${personLabel(person)}` : 'Roles'}
      description={person?.email && person.name ? person.email : 'A change applies within about a minute; they do not need to sign out.'}
      footer={
        <>
          <Button type="button" variant="outline" onClick={confirming ? () => setConfirming(false) : onClose} disabled={saving}>
            {confirming ? 'Back' : 'Cancel'}
          </Button>
          <Button type="button" variant={confirming && plan.remove.length > 0 ? 'destructive' : 'default'} onClick={save} disabled={saving || !changed}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {confirming ? 'Confirm changes' : 'Save'}
          </Button>
        </>
      }
    >
      {person && confirming ? (
        <div className="space-y-3 text-sm" role="alert">
          <p className="font-medium text-foreground">Confirm these changes for {personLabel(person)}:</p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            {toConfirm.map((line) => (
              <li key={line}>{line}</li>
            ))}
            {plan.add
              .filter((key) => !catalog.byKey.get(key)?.confirmGrant)
              .map((key) => (
                <li key={key}>Give {roleLabel(catalog, key)}</li>
              ))}
          </ul>
          <p className="text-xs text-muted-foreground">It applies within about a minute, in every organization, without signing out.</p>
        </div>
      ) : person ? (
        <RoleSections
          key={person.userId}
          catalog={catalog}
          selected={selected}
          locks={editLocks(person, catalog)}
          disabled={saving}
          advancedOpen={holdsSingles}
          onCleanUp={() => setSelected((current) => withoutRedundant(catalog, current))}
          onToggle={(key, checked) =>
            setSelected((current) => {
              const next = new Set(current);
              if (checked) next.add(key);
              else next.delete(key);
              return next;
            })
          }
        />
      ) : null}
    </Sheet>
  );
}

type InviteState = {
  text: string;
  orgId?: string;
  roles: StudioTeamRoleKey[];
  showProblem: boolean;
  sending: boolean;
  results?: StudioTeamInviteResult[];
  busyEmail?: string;
};

/**
 * A new invitation: the organization being shown (or the only one) picked, and
 * the default role (Internal Access) ticked when WorkOS has it.
 */
export function freshInvite(orgs: StudioTeamOrg[], catalog: RoleCatalog, defaultRoleKey: StudioTeamRoleKey | undefined, shownOrgId?: string): InviteState {
  const preselected = defaultRoleKey && catalog.byKey.get(defaultRoleKey)?.available ? [defaultRoleKey] : [];
  const orgId = orgs.some((org) => org.id === shownOrgId) ? shownOrgId : orgs.length === 1 ? orgs[0].id : undefined;
  return { text: '', orgId, roles: preselected, showProblem: false, sending: false };
}

/**
 * Studio Admin → Users. Platform admins give and remove the Studio roles
 * here instead of in the WorkOS dashboard, and invite people into
 * RapidSOS's organizations. The list shows one internal organization at a
 * time, a page at a time, searched on the server. Anyone outside RapidSOS who
 * holds a role is kept out of it and only offered for removal.
 */
export function UsersPage() {
  const [data, setData] = useState<StudioTeamAccessResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [cannotManage, setCannotManage] = useState<CannotManage | undefined>();
  const [chosenOrg, setChosenOrg] = useState<string | undefined>(() => readRememberedOrg());
  const [filter, setFilter] = useState<AccessFilter>('access');
  const [query, setQuery] = useState('');
  const search = useDebouncedValue(query.trim());
  const [roleFilter, setRoleFilter] = useState<StudioTeamRoleKey[]>([]);
  const [pageSize, setPageSize] = useState<number>(PEOPLE_PAGE_SIZES[0]);
  const [people, setPeople] = useState<StudioTeamPeoplePage | undefined>();
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState<string | undefined>();
  const [invitations, setInvitations] = useState<StudioTeamInvitationsPage | undefined>();
  const [busyKey, setBusyKey] = useState<string | undefined>();
  const [editing, setEditing] = useState<StudioTeamPerson | undefined>();
  const [saving, setSaving] = useState(false);
  const [invite, setInvite] = useState<InviteState | undefined>();
  const [revoking, setRevoking] = useState<StudioTeamInvitation | undefined>();
  const [grantingEveryone, setGrantingEveryone] = useState<'confirm' | 'sending' | undefined>();
  const [outsiders, setOutsiders] = useState<{ open: boolean; people?: StudioTeamPerson[]; loading: boolean; error?: string; confirming?: StudioTeamPerson; removing: boolean }>({
    open: false,
    loading: false,
    removing: false,
  });
  const peopleRequest = useRef(0);
  const invitationsRequest = useRef(0);

  const orgs = useMemo(() => data?.orgs ?? [], [data]);
  const orgId = pickOrg(orgs, chosenOrg);
  const orgName = orgs.find((org) => org.id === orgId)?.name ?? 'this organization';
  const searched = searchQuery(search);

  const filtersKey = [orgId, filter, searched, roleFilter.join(','), pageSize].join('\n');
  const [paging, setPaging] = useState({ key: '', page: 1 });
  const page = paging.key === filtersKey ? paging.page : 1;
  const [invitationPaging, setInvitationPaging] = useState({ orgId: '', page: 1 });
  const invitationsPage = invitationPaging.orgId === orgId ? invitationPaging.page : 1;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(undefined);
    try {
      setData(await adminClient.getTeamAccess());
      setCannotManage(undefined);
    } catch (error) {
      const problem = cannotManageRoles(error);
      setCannotManage(problem);
      if (!problem) setLoadError(error instanceof Error ? error.message : 'Failed to load people');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPeople = useCallback(
    async (fresh: boolean) => {
      if (!orgId) return;
      const request = ++peopleRequest.current;
      setPeopleLoading(true);
      setPeopleError(undefined);
      try {
        const result = await adminClient.getTeamPeople({ orgId, access: filter, q: searched, roles: roleFilter, page, pageSize, fresh });
        if (request === peopleRequest.current) setPeople(result);
      } catch (error) {
        if (request !== peopleRequest.current) return;
        const problem = cannotManageRoles(error);
        if (problem) setCannotManage(problem);
        else setPeopleError(error instanceof Error ? error.message : 'Failed to load people');
      } finally {
        if (request === peopleRequest.current) setPeopleLoading(false);
      }
    },
    [orgId, filter, searched, roleFilter, page, pageSize],
  );

  const loadInvitations = useCallback(async () => {
    if (!orgId) return;
    const request = ++invitationsRequest.current;
    try {
      const result = await adminClient.getTeamInvitations(orgId, invitationsPage);
      if (request === invitationsRequest.current) setInvitations(result);
    } catch {
      if (request === invitationsRequest.current) setInvitations(undefined);
    }
  }, [orgId, invitationsPage]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (data && !cannotManage) void loadPeople(false);
  }, [data, cannotManage, loadPeople]);

  useEffect(() => {
    if (data && !cannotManage) void loadInvitations();
  }, [data, cannotManage, loadInvitations]);

  const refreshAll = async () => {
    await Promise.all([load(), loadPeople(true), loadInvitations()]);
  };

  const changeOrg = (next: string) => {
    setChosenOrg(next);
    rememberOrg(next);
    setPeople(undefined);
    setInvitations(undefined);
  };

  const failed = (error: unknown, fallback: string) => {
    const problem = cannotManageRoles(error);
    if (problem) setCannotManage(problem);
    toast.error(error instanceof Error ? error.message : fallback);
  };

  const catalog = useMemo(() => roleCatalog(data), [data]);

  const saveRoles = async (person: StudioTeamPerson, change: { add: StudioTeamRoleKey[]; remove: StudioTeamRoleKey[] }) => {
    const who = personLabel(person);
    setSaving(true);
    try {
      const result = await adminClient.setTeamPersonRoles(person.userId, change);
      if (result.failed.length > 0) {
        toast.error(`Some changes for ${who} did not go through: ${result.failed.map((entry) => roleLabel(catalog, entry.key)).join(', ')}. Try again.`);
      } else {
        toast.success(`Saved ${who}'s roles. They apply within about a minute.`);
      }
      setEditing(undefined);
      await Promise.all([load(), loadPeople(true)]);
    } catch (error) {
      failed(error, 'Failed to change the roles');
    } finally {
      setSaving(false);
    }
  };

  const loadOutsiders = async () => {
    setOutsiders((current) => ({ ...current, open: true, loading: true, error: undefined }));
    try {
      const result = await adminClient.getTeamOutsiders();
      setOutsiders((current) => ({ ...current, loading: false, people: result.people }));
    } catch (error) {
      setOutsiders((current) => ({ ...current, loading: false, error: error instanceof Error ? error.message : 'Failed to load people outside RapidSOS' }));
    }
  };

  const removeOutsiderRoles = async (person: StudioTeamPerson) => {
    const remove = removableRoles(person);
    setOutsiders((current) => ({ ...current, removing: true }));
    try {
      const result = await adminClient.setTeamPersonRoles(person.userId, { add: [], remove });
      if (result.failed.length > 0) {
        toast.error(`Some roles were not removed from ${personLabel(person)}: ${result.failed.map((entry) => roleLabel(catalog, entry.key)).join(', ')}. Try again.`);
      } else {
        toast.success(`Removed ${personLabel(person)}'s Studio roles. It applies within about a minute.`);
      }
      setOutsiders((current) => ({ ...current, removing: false, confirming: undefined }));
      await Promise.all([load(), loadOutsiders()]);
    } catch (error) {
      setOutsiders((current) => ({ ...current, removing: false }));
      failed(error, 'Failed to remove the roles');
    }
  };

  const closeInvite = () => {
    const sent = Boolean(invite?.results);
    setInvite(undefined);
    if (sent) void refreshAll();
  };

  const sendInvites = async (emails: string[], sendToExisting = false) => {
    if (!invite?.orgId) return;
    const { orgId: inviteOrg, roles } = invite;
    setInvite((current) => current && { ...current, sending: true, busyEmail: sendToExisting ? emails[0] : undefined });
    try {
      const { results } = await adminClient.inviteTeamMembers({ emails, orgId: inviteOrg, roles, ...(sendToExisting ? { sendToExisting } : {}) });
      setInvite((current) => {
        if (!current) return current;
        const merged = current.results ? current.results.map((row) => results.find((next) => next.email === row.email) ?? row) : results;
        return { ...current, sending: false, busyEmail: undefined, results: merged };
      });
      const invited = results.filter((result) => result.status === 'invited').length;
      if (invited > 0) toast.success(invited === 1 ? 'Invitation sent.' : `${invited} invitations sent.`);
    } catch (error) {
      setInvite((current) => current && { ...current, sending: false, busyEmail: undefined });
      failed(error, 'Failed to send the invitations');
    }
  };

  const submitInvite = () => {
    if (!invite) return;
    if (inviteFormProblem(invite.text, invite.orgId)) {
      setInvite({ ...invite, showProblem: true });
      return;
    }
    void sendInvites(parseEmailList(invite.text).emails);
  };

  const addDirectly = async (email: string) => {
    if (!invite?.orgId) return;
    const { orgId: inviteOrg, roles } = invite;
    setInvite((current) => current && { ...current, busyEmail: email });
    try {
      const { rolesGiven } = await adminClient.addTeamMember({ email, orgId: inviteOrg, roles });
      setInvite((current) =>
        current && {
          ...current,
          busyEmail: undefined,
          results: current.results?.map((row) => (row.email === email ? { email, status: 'already_member', rolesGiven, verified: true } : row)),
        },
      );
      toast.success(
        rolesGiven.length > 0 ? `Added ${email}. Their roles apply within about a minute.` : `Added ${email}.`,
      );
    } catch (error) {
      setInvite((current) => current && { ...current, busyEmail: undefined });
      failed(error, 'Failed to add this person');
    }
  };

  const defaultRole = data?.defaultRoleKey ? catalog.byKey.get(data.defaultRoleKey) : undefined;
  const missingDefault = people?.missingDefault ?? 0;

  const grantDefaultToEveryone = async () => {
    if (!defaultRole || !orgId) return;
    setGrantingEveryone('sending');
    try {
      const result = await adminClient.grantDefaultTeamRoleToEveryone(orgId);
      if (result.failed.length > 0) {
        toast.error(`Gave ${defaultRole.label} to ${result.given.length} in ${orgName}; WorkOS refused ${result.failed.length}. Try again.`);
      } else {
        toast.success(
          result.given.length === 1
            ? `Gave ${defaultRole.label} to 1 person in ${orgName}. It applies within about a minute.`
            : `Gave ${defaultRole.label} to ${result.given.length} people in ${orgName}. It applies within about a minute.`,
        );
      }
      setGrantingEveryone(undefined);
      await Promise.all([load(), loadPeople(true)]);
    } catch (error) {
      setGrantingEveryone('confirm');
      failed(error, `Failed to give ${defaultRole.label} to everyone in ${orgName}`);
    }
  };

  const resend = async (invitation: StudioTeamInvitation) => {
    setBusyKey(invitationKey(invitation));
    try {
      await adminClient.resendTeamInvitation(invitation.orgId, invitation.email);
      toast.success(`Sent a new invitation to ${invitation.email}.`);
      await loadInvitations();
    } catch (error) {
      failed(error, 'Failed to resend the invitation');
    } finally {
      setBusyKey(undefined);
    }
  };

  const revoke = async (invitation: StudioTeamInvitation) => {
    setBusyKey(invitationKey(invitation));
    try {
      await adminClient.revokeTeamInvitation(invitation.orgId, invitation.email);
      toast.success(`Revoked the invitation to ${invitation.email}.`);
      setRevoking(undefined);
      await Promise.all([load(), loadInvitations()]);
    } catch (error) {
      failed(error, 'Failed to revoke the invitation');
    } finally {
      setBusyKey(undefined);
    }
  };

  return (
    <>
      <UsersView
        data={data}
        loading={loading}
        loadError={loadError}
        cannotManage={cannotManage}
        orgId={orgId}
        onOrgChange={changeOrg}
        people={people}
        peopleLoading={peopleLoading}
        peopleError={peopleError}
        filter={filter}
        onFilterChange={setFilter}
        query={query}
        onQueryChange={setQuery}
        search={search}
        roleFilter={roleFilter}
        onRoleFilterChange={setRoleFilter}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        onPageChange={(next) => setPaging({ key: filtersKey, page: next })}
        invitations={invitations}
        onInvitationsPageChange={(next) => orgId && setInvitationPaging({ orgId, page: next })}
        busyKey={busyKey}
        onEditRoles={setEditing}
        onRetry={() => void (data ? loadPeople(true) : load())}
        onInvite={() => setInvite(freshInvite(orgs, catalog, data?.defaultRoleKey, orgId))}
        onGrantDefaultToEveryone={() => setGrantingEveryone('confirm')}
        onResend={(invitation) => void resend(invitation)}
        onRevoke={setRevoking}
        onReviewOutsiders={() => void loadOutsiders()}
      />
      <OutsidersSheet
        open={outsiders.open}
        people={outsiders.people}
        loading={outsiders.loading}
        error={outsiders.error}
        catalog={catalog}
        confirming={outsiders.confirming}
        removing={outsiders.removing}
        onAskRemove={(person) => setOutsiders((current) => ({ ...current, confirming: person }))}
        onCancelRemove={() => setOutsiders((current) => ({ ...current, confirming: undefined }))}
        onRemove={(person) => void removeOutsiderRoles(person)}
        onClose={() => setOutsiders({ open: false, loading: false, removing: false })}
      />
      <Dialog
        open={Boolean(invite)}
        onClose={closeInvite}
        title="Invite people"
        description="They get an email to join a RapidSOS organization and Studio."
        footer={
          invite?.results ? (
            <Button type="button" onClick={closeInvite} disabled={Boolean(invite.busyEmail)}>
              Done
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={closeInvite} disabled={invite?.sending}>
                Cancel
              </Button>
              <Button type="button" onClick={submitInvite} disabled={invite?.sending}>
                {invite?.sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                Send invitations
              </Button>
            </>
          )
        }
      >
        {invite ? (
          <InviteForm
            orgs={orgs}
            catalog={catalog}
            text={invite.text}
            onTextChange={(text) => setInvite({ ...invite, text })}
            orgId={invite.orgId}
            onOrgChange={(orgId) => setInvite({ ...invite, orgId })}
            roles={invite.roles}
            onToggleRole={(key) =>
              setInvite({ ...invite, roles: invite.roles.includes(key) ? invite.roles.filter((role) => role !== key) : [...invite.roles, key] })
            }
            onCleanUpRoles={() => setInvite({ ...invite, roles: [...withoutRedundant(catalog, new Set(invite.roles))] })}
            showProblem={invite.showProblem}
            results={invite.results}
            busyEmail={invite.busyEmail}
            onAddDirectly={(email) => void addDirectly(email)}
            onInviteAnyway={(email) => void sendInvites([email], true)}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={Boolean(revoking)}
        onClose={() => setRevoking(undefined)}
        title={`Revoke the invitation to ${revoking?.email ?? ''}?`}
        description="The link in their email stops working, and the Studio access and roles promised with it are withdrawn."
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setRevoking(undefined)} disabled={Boolean(busyKey)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => revoking && void revoke(revoking)} disabled={Boolean(busyKey)}>
              {busyKey ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Revoke invitation
            </Button>
          </>
        }
      >
        {revoking ? <RoleTags roles={revoking.roles} catalog={catalog} /> : null}
      </Dialog>
      <Dialog
        open={Boolean(grantingEveryone)}
        onClose={() => grantingEveryone !== 'sending' && setGrantingEveryone(undefined)}
        title={`Give ${defaultRole?.label ?? 'the default role'} to ${missingDefault} ${missingDefault === 1 ? 'person' : 'people'} in ${orgName}?`}
        description={bulkGrantDescription(orgName, orgs.length)}
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setGrantingEveryone(undefined)} disabled={grantingEveryone === 'sending'}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void grantDefaultToEveryone()} disabled={grantingEveryone === 'sending' || missingDefault === 0}>
              {grantingEveryone === 'sending' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Give to {missingDefault}
            </Button>
          </>
        }
      >
        {defaultRole ? <p className="text-sm text-muted-foreground">{defaultRole.description}</p> : null}
      </Dialog>
      <EditRolesSheet person={editing} catalog={catalog} saving={saving} onClose={() => setEditing(undefined)} onSave={(person, change) => void saveRoles(person, change)} />
    </>
  );
}
